import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import useSWR from 'swr';
import { Grid, GridUnit } from '@hookdeck/theme';
import { organization_levels_by_role } from '../../../../../../../domain/roles';
import { OperatorConfigUpdateInput } from '../../../../../../../typings/OutpostDeployment.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import Button from '../../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../../common/base/Card';
import Container from '../../../common/base/Container';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import { useDialog } from '../../../common/Dialog';
import MultiTokenInput from '../../../common/Form/Fields/MultiTokenInput';
import RadioCardsInput from '../../../common/Form/Fields/RadioCardsInput';
import SelectInput from '../../../common/Form/Fields/SelectInput';
import SwitchInput from '../../../common/Form/Fields/SwitchInput';
import TextInput from '../../../common/Form/Fields/TextInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { DashboardContext } from '../../DashboardScene/DashboardContext';
import { useOutpostConfigUpdate } from '../OutpostContext';

// Parse comma-separated string to array
const parseTopics = (value: string | null | undefined): string[] => {
  if (!value) return [];
  return value.split(',').map(t => t.trim()).filter(Boolean);
};

// Serialize array to comma-separated string
const serializeTopics = (topics: string[]): string => {
  return topics.join(',');
};
const TopicsSection: React.FC = () => {
  const $ = _c(12);
  const {
    config,
    updateConfig
  } = useOutpostConfigUpdate();
  let t0;
  if ($[0] !== config.TOPICS) {
    t0 = parseTopics(config.TOPICS);
    $[0] = config.TOPICS;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const initial_topics = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      b: 10
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = config.TOPICS_ALLOW_WILDCARDS === "true";
  let t3;
  if ($[3] !== initial_topics || $[4] !== t2) {
    t3 = {
      topics: initial_topics,
      allow_wildcards: t2
    };
    $[3] = initial_topics;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== config.TOPICS_ALLOW_WILDCARDS || $[7] !== updateConfig) {
    t4 = async values => {
      await updateConfig({
        TOPICS: serializeTopics(values.topics),
        ...(values.allow_wildcards !== (config.TOPICS_ALLOW_WILDCARDS === "true") && {
          TOPICS_ALLOW_WILDCARDS: String(values.allow_wildcards)
        })
      }, {
        success: "Topics updated",
        failure: "Failed to update topics"
      });
    };
    $[6] = config.TOPICS_ALLOW_WILDCARDS;
    $[7] = updateConfig;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <Container medium={true} left={true} m={t1}><Formik enableReinitialize={true} initialValues={t3} onSubmit={t4}>{_temp}</Formik></Container>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const parseRetrySchedule = (value: string | null | undefined): string[] => {
  if (!value) return [];
  return value.split(',').map(t => t.trim()).filter(Boolean);
};
const serializeRetrySchedule = (schedule: string[]): string => {
  return schedule.join(',');
};
const getIdExampleSuffix = (idgen_type: string): string => {
  switch (idgen_type) {
    case 'uuidv4':
      return '550e8400-e29b-41d4-a716-446655440000';
    case 'uuidv7':
      return '0195f0fd-51ce-7a5b-8d8a-12f66f7e6c31';
    default:
      return 'a1B2c3D4e5F6g7H8i9J0';
  }
};
const getIdExample = (prefix: string | undefined, idgen_type: string): string => {
  return `${prefix || ''}${getIdExampleSuffix(idgen_type)}`;
};
const RetrySection: React.FC = () => {
  const $ = _c(13);
  const {
    config,
    updateConfig
  } = useOutpostConfigUpdate();
  const has_schedule = !!config.RETRY_SCHEDULE;
  const initial_mode = has_schedule ? "fixed" : "exponential";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      b: 10
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = config.RETRY_INTERVAL_SECONDS ?? "";
  const t2 = config.MAX_RETRY_LIMIT ?? "";
  let t3;
  if ($[1] !== config.RETRY_SCHEDULE) {
    t3 = parseRetrySchedule(config.RETRY_SCHEDULE);
    $[1] = config.RETRY_SCHEDULE;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== initial_mode || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      retry_mode: initial_mode,
      RETRY_INTERVAL_SECONDS: t1,
      MAX_RETRY_LIMIT: t2,
      retry_schedule: t3
    };
    $[3] = initial_mode;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== updateConfig) {
    t5 = async (values, t6) => {
      const {
        resetForm
      } = t6;
      const updated_config = {};
      if (values.retry_mode === "exponential") {
        updated_config.RETRY_INTERVAL_SECONDS = values.RETRY_INTERVAL_SECONDS || null;
        updated_config.MAX_RETRY_LIMIT = values.MAX_RETRY_LIMIT || null;
        updated_config.RETRY_SCHEDULE = null;
      } else {
        updated_config.RETRY_SCHEDULE = serializeRetrySchedule(values.retry_schedule);
        updated_config.RETRY_INTERVAL_SECONDS = null;
        updated_config.MAX_RETRY_LIMIT = null;
      }
      const result = await updateConfig(updated_config, {
        success: "Retry settings updated",
        failure: "Failed to update retry settings",
        labels: {
          RETRY_INTERVAL_SECONDS: "Base interval",
          MAX_RETRY_LIMIT: "Max retries",
          RETRY_SCHEDULE: "Retry schedule"
        }
      });
      if (!result) {
        return;
      }
      const result_has_schedule = !!result.RETRY_SCHEDULE;
      resetForm({
        values: {
          retry_mode: result_has_schedule ? "fixed" : "exponential",
          RETRY_INTERVAL_SECONDS: result.RETRY_INTERVAL_SECONDS ?? "",
          MAX_RETRY_LIMIT: result.MAX_RETRY_LIMIT ?? "",
          retry_schedule: parseRetrySchedule(result.RETRY_SCHEDULE)
        }
      });
    };
    $[8] = updateConfig;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <Container medium={true} left={true} m={t0}><Formik enableReinitialize={true} initialValues={t4} onSubmit={t5}>{_temp2}</Formik></Container>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
const AdvancedSection: React.FC = () => {
  const $ = _c(21);
  const {
    config,
    updateConfig
  } = useOutpostConfigUpdate();
  const [expanded, setExpanded] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      b: 10
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "center",
      gap: 2
    };
    t2 = {
      cursor: "pointer"
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] !== expanded) {
    t3 = () => setExpanded(!expanded);
    $[3] = expanded;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text bold={true} size="l" as="h2" m={0}>Advanced Configuration</Text>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = expanded ? "expand_less" : "expand_more";
  let t6;
  if ($[6] !== t5) {
    t6 = <Icon icon={t5} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t6) {
    t7 = <Div flex={t1} style={t2} onClick={t3}>{t4}{t6}</Div>;
    $[8] = t3;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = expanded ? 4 : 0;
  let t9;
  if ($[11] !== t8) {
    t9 = <Text m={{
      b: t8
    }} muted={true}>Configuration that rarely needs to be changed and should be approached with caution.</Text>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== config || $[14] !== expanded || $[15] !== updateConfig) {
    t10 = expanded && <Formik enableReinitialize={true} initialValues={{
      MAX_DESTINATIONS_PER_TENANT: config.MAX_DESTINATIONS_PER_TENANT ?? "",
      IDGEN_TYPE: config.IDGEN_TYPE ?? "",
      IDGEN_EVENT_PREFIX: config.IDGEN_EVENT_PREFIX ?? "",
      IDGEN_DESTINATION_PREFIX: config.IDGEN_DESTINATION_PREFIX ?? "",
      IDGEN_ATTEMPT_PREFIX: config.IDGEN_ATTEMPT_PREFIX ?? ""
    }} onSubmit={async (values, t11) => {
      const {
        resetForm
      } = t11;
      const updated_config = {};
      updated_config.MAX_DESTINATIONS_PER_TENANT = values.MAX_DESTINATIONS_PER_TENANT || null;
      updated_config.IDGEN_TYPE = values.IDGEN_TYPE || null;
      updated_config.IDGEN_EVENT_PREFIX = values.IDGEN_EVENT_PREFIX;
      updated_config.IDGEN_DESTINATION_PREFIX = values.IDGEN_DESTINATION_PREFIX;
      updated_config.IDGEN_ATTEMPT_PREFIX = values.IDGEN_ATTEMPT_PREFIX;
      const result = await updateConfig(updated_config, {
        success: "Advanced settings updated",
        failure: "Failed to update advanced settings"
      });
      if (!result) {
        return;
      }
      resetForm({
        values: {
          MAX_DESTINATIONS_PER_TENANT: result.MAX_DESTINATIONS_PER_TENANT ?? "",
          IDGEN_TYPE: result.IDGEN_TYPE ?? "",
          IDGEN_EVENT_PREFIX: result.IDGEN_EVENT_PREFIX ?? "",
          IDGEN_DESTINATION_PREFIX: result.IDGEN_DESTINATION_PREFIX ?? "",
          IDGEN_ATTEMPT_PREFIX: result.IDGEN_ATTEMPT_PREFIX ?? ""
        }
      });
    }}>{_temp3}</Formik>;
    $[13] = config;
    $[14] = expanded;
    $[15] = updateConfig;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t7 || $[19] !== t9) {
    t11 = <Container medium={true} left={true} m={t0}>{t7}{t9}{t10}</Container>;
    $[17] = t10;
    $[18] = t7;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
};
const OutpostSettings: React.FC = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <TopicsSection />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Div m={{
      x: 6,
      b: 8
    }}><Divider /></Div>;
    t2 = <RetrySection />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <>{t0}{t1}{t2}<Div m={{
        x: 6,
        b: 8
      }}><Divider /></Div><AdvancedSection /></>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};

// Hoisted out of TeamSettings' transfer-dialog config: an inline component that calls hooks
// (useContext/useSWR) inside a function expression makes the React Compiler bail on the whole
// parent. As a top-level component it memoizes cleanly.
const TransferProjectFields: React.FC<{
  current_organization_id?: string;
}> = t0 => {
  const $ = _c(29);
  const {
    current_organization_id
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = APIMethodKeys.organizations.listAll();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== HookdeckAPI.organizations) {
    t2 = () => HookdeckAPI.organizations.listAll();
    $[1] = HookdeckAPI.organizations;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: organizations
  } = useSWR(t1, t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Text m={{
      b: 4
    }}>Admins and project admins are able to transfer projects to organizations where they are an admin. Projects maintain their access status (Open or Private) in the organization they are transferred to.</Text>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Text bold={true} m={{
      b: 4
    }}>Transfer considerations:</Text>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      align: "flex-start"
    };
    t6 = {
      b: 4
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon="lock" left={true} m={{
      t: 0.5
    }} />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Div flex={t5} m={t6}>{t7}<Div><Text semi={true}>Project access</Text><Text muted={true}>Project access is not maintained when transferring a project to an organization. If the project is private all members will lose access and will need to be re-added</Text></Div></Div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t10;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      align: "flex-start"
    };
    t10 = {
      b: 4
    };
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Icon icon="construction" left={true} m={{
      t: 0.5
    }} />;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Div flex={t9} m={t10}>{t11}<Div><Text semi={true}>Features</Text><Text muted={true}>Projects inherit features from their parent organizations. If you move this project to an organization with a lesser plan, you may lose feature access.</Text></Div></Div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      align: "flex-start"
    };
    t14 = {
      b: 4
    };
    $[13] = t13;
    $[14] = t14;
  } else {
    t13 = $[13];
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Icon icon="progress_activity" left={true} m={{
      t: 0.5
    }} />;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Div flex={t13} m={t14}>{t15}<Div><Text semi={true}>No downtime</Text><Text muted={true}>You won't experience any downtime when transferring a project.</Text></Div></Div>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  let t18;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      align: "flex-start"
    };
    t18 = {
      b: 4
    };
    $[17] = t17;
    $[18] = t18;
  } else {
    t17 = $[17];
    t18 = $[18];
  }
  let t19;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Icon icon="rate_limit" left={true} m={{
      t: 0.5
    }} />;
    $[19] = t19;
  } else {
    t19 = $[19];
  }
  let t20;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Div flex={t17} m={t18}>{t19}<Div><Text semi={true}>Throughput transfer</Text><Text muted={true}>If the target organization is on a paid plan, the project's throughput and fee will be transfered to the new organization. If the organization is on a free plan, the project throughput will be reset to the free plan limits.</Text></Div></Div>;
    $[20] = t20;
  } else {
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Divider m={{
      y: 6
    }} />;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      grow: true,
      direction: "column"
    };
    $[22] = t22;
  } else {
    t22 = $[22];
  }
  let t23;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      parent_width: true,
      placement: "bottom-end"
    };
    $[23] = t23;
  } else {
    t23 = $[23];
  }
  let t24;
  if ($[24] !== current_organization_id || $[25] !== organizations) {
    t24 = organizations?.filter(organization => organization_levels_by_role[organization.role] >= organization_levels_by_role.admin && organization.id !== current_organization_id).map(_temp4) || [];
    $[24] = current_organization_id;
    $[25] = organizations;
    $[26] = t24;
  } else {
    t24 = $[26];
  }
  let t25;
  if ($[27] !== t24) {
    t25 = <>{t3}{t4}{t8}{t12}{t16}{t20}{t21}<SelectInput block={true} label="Target organization" help="The organization you'd like your project transferred to." placeholder="Select target organization" name="organization_id" flex={t22} search={true} dropdown={t23} options={t24} /></>;
    $[27] = t24;
    $[28] = t25;
  } else {
    t25 = $[28];
  }
  return t25;
};
const TeamSettings: React.FC = () => {
  const $ = _c(40);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    team,
    mutateTeam,
    resetContext
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  let t0;
  if ($[0] !== HookdeckAPI.teams || $[1] !== addToast || $[2] !== mutateTeam) {
    t0 = values => HookdeckAPI.teams.update(values).then(team_0 => {
      addToast("success", "Project updated");
      mutateTeam(team_0);
      return true;
    }).catch(() => {
      addToast("error", "Failed to update project");
    });
    $[0] = HookdeckAPI.teams;
    $[1] = addToast;
    $[2] = mutateTeam;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleSubmit = t0;
  let t1;
  if ($[4] !== HookdeckAPI.teams || $[5] !== addToast || $[6] !== resetContext || $[7] !== showDialog || $[8] !== team.organization_id) {
    t1 = () => showDialog(values_0 => HookdeckAPI.teams.transfer(values_0.organization_id).then(() => {
      resetContext();
    }).catch(() => {
      addToast("error", "The project could not be transferred, please contact us.");
    }), undefined, {
      title: "Transfer project",
      submit_label: "Transfer project",
      cancel_label: "Cancel",
      form_props: {
        initial_values: {
          organization_id: ""
        },
        validate: _temp5,
        Fields: () => <TransferProjectFields current_organization_id={team?.organization_id} />
      }
    });
    $[4] = HookdeckAPI.teams;
    $[5] = addToast;
    $[6] = resetContext;
    $[7] = showDialog;
    $[8] = team.organization_id;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const onTransfer = t1;
  let t2;
  if ($[10] !== HookdeckAPI.teams || $[11] !== addToast || $[12] !== resetContext || $[13] !== showDialog || $[14] !== team.name) {
    t2 = () => showDialog(() => HookdeckAPI.teams.delete().then(() => {
      resetContext();
    }).catch(() => {
      addToast("error", "The project could not be deleted, please contact us.");
    }), undefined, {
      title: "Delete project",
      submit_label: "Delete project",
      cancel_label: "Cancel",
      submit_icon: "delete",
      danger: true,
      form_props: {
        initial_values: {
          name: ""
        },
        validate: t3 => {
          const {
            name
          } = t3;
          if (!name || name.length < 0) {
            return {
              name: "Required"
            };
          }
          if (name !== team.name) {
            return {
              name: "Project name does not match."
            };
          }
          return {};
        },
        Fields: () => <><Text m={{
            b: 4
          }}>This action can't be undone. You'll lose all data associated with this project, including configuration, tenants, destinations and events. Are you sure? <br /><br />If you'd like to delete this project, type <strong>{team.name}</strong> below to confirm.</Text><TextInput m={0} name="name" placeholder={team.name} required={true} focus={true} /></>
      }
    });
    $[10] = HookdeckAPI.teams;
    $[11] = addToast;
    $[12] = resetContext;
    $[13] = showDialog;
    $[14] = team.name;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const onDelete = t2;
  let t3;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 10
    };
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== team.name) {
    t4 = {
      name: team.name
    };
    $[17] = team.name;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== team.name) {
    t5 = props => <Form><Text bold={true} size="l" as="h2" m={{
        t: 0,
        b: 4
      }}>Project name</Text><TextInput name="name" required={true} m={{
        b: 5
      }} /><Button.Permission primary={true} disabled={!props.isValid || !props.touched || props.isSubmitting || team.name === props.values.name} submit={true} icon={props.isSubmitting ? "loading" : "save"}>Save</Button.Permission></Form>;
    $[19] = team.name;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== handleSubmit || $[22] !== t4 || $[23] !== t5) {
    t6 = <Container medium={true} left={true} m={t3}><Formik initialValues={t4} validate={_temp6} onSubmit={handleSubmit}>{t5}</Formik></Container>;
    $[21] = handleSubmit;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  let t8;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Div m={{
      x: 6,
      b: 8
    }}><Divider /></Div>;
    t8 = <OutpostSettings />;
    $[25] = t7;
    $[26] = t8;
  } else {
    t7 = $[25];
    t8 = $[26];
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Div m={{
      x: 6,
      b: 8
    }}><Divider /></Div>;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      b: 10
    };
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Text bold={true} size="l" as="h2" m={{
      b: 0
    }}>Transfer project</Text>;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Text m={{
      b: 4
    }} muted={true}>Move this project and all associated data to a different organization.</Text>;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== onTransfer) {
    t13 = <Container medium={true} left={true} m={t10}>{t11}{t12}<Button.Permission role="admin" icon="transfer" outline={true} onClick={onTransfer}>Transfer project</Button.Permission></Container>;
    $[31] = onTransfer;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== onDelete || $[34] !== team.directory_sync_enabled) {
    t14 = !team.directory_sync_enabled && <><Div m={{
        x: 6,
        b: 8
      }}><Divider /></Div><Container medium={true} left={true} m={{
        b: 10
      }}><Text bold={true} size="l" as="h2" m={0}>Delete project</Text><Text m={{
          b: 4
        }} muted={true}>Permanently delete this project and all associated data.</Text><Button.Permission role="admin" outline={true} danger={true} icon="delete" onClick={onDelete}>Delete project</Button.Permission></Container></>;
    $[33] = onDelete;
    $[34] = team.directory_sync_enabled;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14 || $[38] !== t6) {
    t15 = <>{t6}{t7}{t8}{t9}{t13}{t14}</>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t6;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};
export default TeamSettings;
function _temp(props) {
  return <Form><Text bold={true} size="l" as="h2" m={{
      b: 0
    }}>Topics</Text><Text m={{
      b: 4
    }} muted={true}>Set the list of supported topics for your events.</Text><MultiTokenInput name="topics" placeholder="Type or paste topics..." help="Enter topic names (e.g., issue.opened, payment.completed). Add in bulk by pasting a list." />{props.values.topics.length > 0 && <StyledCard m={{
      b: 5
    }}><StyledCardSection flex={{
        justify: "flex-start",
        align: "center",
        gap: 3
      }} p={{
        x: 4,
        y: 3
      }}><SwitchInput name="allow_wildcards" /><Div flex={{
          align: "center",
          gap: 1
        }}><Text>Allow topic subscriptions with wildcards</Text><Tooltip tooltip={<>Allow destination subscriptions to use <code>*</code> to match multiple topics. For example, <code>payment.*</code> matches both{" "}<code>payment.created</code> and <code>payment.completed</code>.</>}><Icon muted={true} icon="info" /></Tooltip></Div></StyledCardSection></StyledCard>}<Button.Permission primary={props.dirty} m={{
      t: props.values.topics.length === 0 ? 1 : 0
    }} disabled={!props.isValid || !props.dirty || props.isSubmitting} submit={true} icon={props.isSubmitting ? "loading" : "save"}>Save</Button.Permission></Form>;
}
function _temp2(props) {
  return <Form><Text bold={true} size="l" as="h2" m={{
      b: 0
    }}>Retry Strategy</Text><Text m={{
      b: 4
    }} muted={true}>Configure how failed deliveries are retried.</Text><RadioCardsInput name="retry_mode" m={{
      b: 4
    }} options={[{
      value: "exponential",
      label: "Exponential Backoff",
      description: "Retries with increasing delays (base 2)"
    }, {
      value: "fixed",
      label: "Fixed Schedule",
      description: "Retries at specific intervals"
    }]} />{props.values.retry_mode === "exponential" && <Grid gap={4}><GridUnit size={0.5}><TextInput name="RETRY_INTERVAL_SECONDS" label="Base interval (seconds)" help="Base delay in seconds, doubles each retry" type="number" placeholder="30" /></GridUnit><GridUnit size={0.5}><TextInput name="MAX_RETRY_LIMIT" label="Max retries" help="Maximum number of retry attempts" type="number" placeholder="10" /></GridUnit></Grid>}{props.values.retry_mode === "fixed" && <MultiTokenInput name="retry_schedule" placeholder="Enter delay in seconds and press enter..." help="Fixed delays in seconds between retries (e.g., 5, 60, 600, 3600)" allow_duplicates={true} />}<Button.Permission primary={true} disabled={!props.dirty || props.isSubmitting} submit={true} icon={props.isSubmitting ? "loading" : "save"}>Save</Button.Permission></Form>;
}
function _temp3(props) {
  return <Form><TextInput name="MAX_DESTINATIONS_PER_TENANT" label="Max destinations per tenant" help={"Maximum number of destinations a tenant can have (1\u20131000). Lower limit results in better performance."} type="number" placeholder="20" /><Text m={{
      t: 6
    }} bold={true}>ID Generation</Text><Text m={{
      b: 4
    }} muted={true}>Changes are not retroactive and previous IDs will not be updated.</Text><SelectInput block={true} name="IDGEN_TYPE" label="ID type" help={`Example format: ${getIdExample(props.values.IDGEN_EVENT_PREFIX, props.values.IDGEN_TYPE)}`} options={[{
      value: "uuidv4",
      label: "UUIDv4 (Random UUID)"
    }, {
      value: "uuidv7",
      label: "UUIDv7 (Time-sorted UUID)"
    }, {
      value: "nanoid",
      label: "NanoID (Short, URL-friendly)"
    }]} /><TextInput name="IDGEN_DESTINATION_PREFIX" label="Destination ID prefix" help={getIdExample(props.values.IDGEN_DESTINATION_PREFIX, props.values.IDGEN_TYPE)} mono={true} /><TextInput name="IDGEN_EVENT_PREFIX" label="Event ID prefix" help={getIdExample(props.values.IDGEN_EVENT_PREFIX, props.values.IDGEN_TYPE)} mono={true} /><TextInput name="IDGEN_ATTEMPT_PREFIX" label="Attempt ID prefix" help={getIdExample(props.values.IDGEN_ATTEMPT_PREFIX, props.values.IDGEN_TYPE)} mono={true} /><Button.Permission primary={true} disabled={!props.dirty || props.isSubmitting} submit={true} m={{
      t: 1
    }} icon={props.isSubmitting ? "loading" : "save"}>Save</Button.Permission></Form>;
}
function _temp4(organization_0) {
  return {
    label: organization_0.name,
    value: organization_0.id
  };
}
function _temp5(t0) {
  const {
    organization_id
  } = t0;
  if (!organization_id) {
    return {
      organization_id: "Required"
    };
  }
  return {};
}
function _temp6(values_1) {
  const errors = {};
  if (!values_1.name || values_1.name.length === 0) {
    errors.name = "Required";
  }
  if (values_1.name && values_1.name.length > 40) {
    errors.name = "Name is longer than 40 characters";
  }
  return errors;
}